import React from 'react';
import { Search, MessageCircle, CalendarDays, Lock, Package, Star } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { SectionWrapper, SectionHeading } from '../../components/common';
import { getVisualCmsIcon } from '@/lib/visual-cms/icon-registry';
import { CardDescriptionHtml } from '@/components/visual-cms/CardDescriptionHtml';

interface Step {
  num: number;
  icon: LucideIcon;
  label: string;
  desc: string;
}

export interface StepInput {
  /** Lucide registry key (e.g. "Compass"). */
  icon?: string | null;
  title: string;
  desc: string;
}

export interface StepsSectionProps {
  title?: string;
  steps?: StepInput[];
}

const defaultSteps: Array<Omit<Step, 'num'>> = [
  { icon: Search, label: 'Search', desc: 'Discover items available from trusted members nearby.' },
  { icon: MessageCircle, label: 'Connect', desc: 'Connect instantly through in-app chat to confirm details.' },
  { icon: CalendarDays, label: 'Book', desc: 'Enjoy a smooth and secure booking experience through the platform.' },
  { icon: Lock, label: 'Pickup', desc: 'Meet for a seamless handover and start your rental.' },
  { icon: Package, label: 'Enjoy', desc: 'Benefit from the items your community chooses to share.' },
  { icon: Star, label: 'Return & Review', desc: 'Return the item with care and strengthen the community through your feedback.' },
];

export const StepsSection: React.FC<StepsSectionProps> = ({ title, steps: cmsSteps }) => {
  const heading = title?.trim() || 'How It Works';
  const steps: Step[] =
    cmsSteps && cmsSteps.length > 0
      ? cmsSteps.map((s, i) => ({
          num: i + 1,
          icon: getVisualCmsIcon(s.icon ?? null) ?? defaultSteps[i % defaultSteps.length].icon,
          label: s.title || defaultSteps[i % defaultSteps.length].label,
          desc: s.desc || defaultSteps[i % defaultSteps.length].desc,
        }))
      : defaultSteps.map((s, i) => ({ ...s, num: i + 1 }));

  return (
    <SectionWrapper bg="white" py="xl" className='pt-0'>
      <SectionHeading title={heading} />
      <div className="relative grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-10">
        {steps.map(({ num, icon: StepIcon, label, desc }, i) => (
          <div key={num} className="relative flex flex-col items-center text-center">
            {/* Numbered green circle */}
            <div className="relative z-10 w-7 h-7 rounded-full bg-v2-green-500 text-white flex items-center justify-center font-bold text-[12px] mb-1.5 shadow-sm">
              {num}
            </div>

            {/* Connector line — between step number circles */}
            {i < steps.length - 1 && (
              <div className="hidden lg:block absolute top-[64px] left-[calc(50%+32px)] right-[calc(-50%+18px)] border-t border-[#1E8C3A] border-dotted border-v2-neutral-300 z-0" />
            )}
            <div className="w-14 h-14 rounded-full bg-v2-neutral-50 border border-v2-neutral-100 flex items-center justify-center mb-3 text-v2-green-500">
              <StepIcon size={32} strokeWidth={1.8} />
            </div>

            <p className="font-semibold text-[18px] text-v2-neutral-900 mb-1.5">{label}</p>
            <CardDescriptionHtml
              as="p"
              text={desc}
              className="text-[14px] text-v2-neutral-500 leading-snug max-w-[140px]"
            />
          </div>
        ))}
      </div>
    </SectionWrapper>
  );
};
